<template>
  <div v-if="files && files.length > 0" class="flex flex-wrap gap-1 pt-1">
    <div 
      v-for="file in files" 
      :key="file.id" 
      :data-file-id="file.id"
      class="flex items-center gap-1 px-2 py-1 bg-gpx_gray-900 rounded-md border border-gpx_gray-800 text-xs hover:bg-gpx_gray-900 transition-colors"
    >
      <div class="w-3 h-3 text-blue-600">
        <svg fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
        </svg>
      </div>
      <button
        @click="handleDownload(file)"
        @keydown="handleKeyDown"
        :disabled="downloadingFiles.has(file.id)"
        class="text-gpx_gray-600 hover:text-blue-600 hover:underline focus:outline-none focus:underline focus:text-blue-600 focus:font-medium truncate max-w-24"
        :aria-label="`Download file ${file.original_name}`"
        tabindex="0"
      >
        {{ file.original_name }}
      </button>
      <span v-if="downloadingFiles.has(file.id)" class="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></span>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { getCookie } from '@/utils/cookie';
import service from '@/services';
import { downloadFile } from '@/utils/blob.js';
import * as events from '@/events';

const props = defineProps({
  files: {
    type: Array,
    required: true
  }
});

const downloadingFiles = ref(new Set());

const handleDownload = async (file) => {
  if (file.download_url) {
    events.trigger(events.names.SCOUT_FILE_DOWNLOADED);
    downloadingFiles.value.add(file.id);
    
    try {
      const url = new URL(file.download_url);
      let relativePath = url.pathname + url.search;
      
      if (relativePath.startsWith('/api/')) {
        relativePath = relativePath.substring(4);
      }
      
      const response = await service.get(relativePath, {
        responseType: 'blob',
      });

      downloadFile({ 
        res: response, 
        fileName: file.original_name 
      });
      
    } catch (error) {
      let errorMessage = 'Error downloading file';
      if (error.response?.status) {
        errorMessage += ` (HTTP ${error.response.status})`;
      }
      if (error.response?.statusText) {
        errorMessage += `: ${error.response.statusText}`;
      }
      
      alert(errorMessage);
    } finally {
      downloadingFiles.value.delete(file.id);
    }
  }
};

const handleKeyDown = (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    const button = event.target.closest('button');
    if (button && !button.disabled) {
      const fileId = button.closest('[data-file-id]')?.dataset.fileId;
      if (fileId) {
        const file = props.files.find(f => f.id.toString() === fileId);
        if (file) {
          handleDownload(file);
        }
      }
    }
  }
};
</script> 